import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { notificationService } from '../services/notifications/notificationsService.js';
import { authService } from '../services/auth/authService.js';

const HIDDEN_NOTIFICATION_TYPES = new Set(['driver_location_update']);
const NOTIFICATION_REFRESH_INTERVAL_MS = 30000;
const MAX_NOTIFICATION_REFRESH_BACKOFF_MS = 5 * 60 * 1000;

const getNotificationType = (notification) =>
  notification?.notification_type || notification?.type || null;

const isHiddenNotification = (notification) =>
  HIDDEN_NOTIFICATION_TYPES.has(getNotificationType(notification));

const mergeUniqueNotifications = (...groups) => {
  const seenIds = new Set();
  const merged = [];

  groups.flat().forEach((notification) => {
    const id = notification?.id;
    const key = id === null || id === undefined ? null : String(id);
    if (key !== null && seenIds.has(key)) return;
    if (key !== null) seenIds.add(key);
    merged.push(notification);
  });

  return merged.slice(0, 150);
};

const isBrowserOnline = () => (
  typeof navigator === 'undefined' || navigator.onLine !== false
);

const isTemporaryRefreshError = (error) => {
  const status = Number(error?.status);
  return !Number.isFinite(status)
    || status === 408
    || status === 425
    || status === 429
    || status >= 500;
};

export const useNotifications = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState({});
  const [notifications, setNotifications] = useState([]);
  const unreadCount = useMemo(
    () => notifications.filter((notification) => !notification.is_read).length,
    [notifications],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const notificationListenerRef = useRef(null);
  const lifecycleGenerationRef = useRef(0);
  const liveNotificationSequenceRef = useRef(0);
  const liveNotificationsRef = useRef([]);
  const sessionActiveRef = useRef(false);
  const refreshInFlightRef = useRef(null);
  const refreshAbortControllerRef = useRef(null);
  const queuedFirstPageRefreshRef = useRef(false);
  const fetchNotificationsRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const refreshFailureCountRef = useRef(0);
  const scheduleRefreshRef = useRef(() => {});
  const browserWasOfflineRef = useRef(!isBrowserOnline());


  // Remove notification by ID (dismiss from backend)
  const removeNotification = useCallback(async (id) => {
    try {
      // Find the notification to get its backend ID
      const notification = notifications.find(n => n.id === id);
      if (notification && notification.id) {
        // Dismiss from backend
        await notificationService.dismissNotification(notification.id);
      }

      // Remove from local state
      setNotifications(prev => prev.filter(notification => notification.id !== id));
    } catch {
      console.error('Failed to dismiss notification');
      // Still remove from local state even if backend fails
      setNotifications(prev => prev.filter(notification => notification.id !== id));
    }
  }, [notifications]);

  // Clear all notifications (dismiss all from backend)
  const clearNotifications = useCallback(async () => {
    try {
      await notificationService.dismissAllNotifications();
      setNotifications([]);
      setCurrentPage(1);
      setHasMore(false);
    } catch {
      console.error('Failed to dismiss all notifications');
      // Still clear local state even if backend fails
      setNotifications([]);
      setCurrentPage(1);
      setHasMore(false);
    }
  }, []);

  // Keep all notification list reads single-flight. Recovery requests that
  // arrive during an older read are collapsed into exactly one follow-up read.
  const fetchNotifications = useCallback((
    page = 1,
    append = false,
    generation = lifecycleGenerationRef.current,
    { queueIfBusy = false } = {},
  ) => {
    if (
      !sessionActiveRef.current
      || generation !== lifecycleGenerationRef.current
      || !authService.isAuthenticated()
      || !isBrowserOnline()
    ) {
      return Promise.resolve(null);
    }

    if (refreshInFlightRef.current) {
      if (page === 1 && !append && queueIfBusy) {
        queuedFirstPageRefreshRef.current = true;
      }
      return refreshInFlightRef.current;
    }

    if (page === 1 && !append) {
      // Consume any recovery marker left by an aborted/offline request before
      // starting the replacement read.
      queuedFirstPageRefreshRef.current = false;
    }

    const liveSequenceAtStart = liveNotificationSequenceRef.current;
    const controller = new AbortController();
    refreshAbortControllerRef.current = controller;

    const request = (async () => {
      try {
        if (!append) setIsLoading(true);
        else setIsLoadingMore(true);

        const data = await notificationService.fetchUserNotifications({
          page,
          page_size: 30, // Backend NotificationPagination (max: 150)
          signal: controller.signal,
        });

        if (
          data
          && !controller.signal.aborted
          && generation === lifecycleGenerationRef.current
        ) {
          const newNotifications = (data.results || []).filter((n) => !isHiddenNotification(n));
          const liveNotificationsDuringRequest = liveNotificationsRef.current
            .filter((entry) => entry.sequence > liveSequenceAtStart)
            .map((entry) => entry.notification);
          setNotifications((previous) => (
            append
              ? mergeUniqueNotifications(previous, newNotifications)
              : mergeUniqueNotifications(liveNotificationsDuringRequest, newNotifications)
          ));
          setHasMore(!!data.next); // Check if there are more pages
          setCurrentPage(page);
          setError(null);

          if (page === 1 && !append) {
            refreshFailureCountRef.current = 0;
            scheduleRefreshRef.current(NOTIFICATION_REFRESH_INTERVAL_MS);
          }
        }

        return data;
      } catch (error) {
        if (
          generation !== lifecycleGenerationRef.current
          || controller.signal.aborted
          || error?.name === 'AbortError'
        ) {
          return null;
        }

        console.error('Failed to fetch notifications', {
          status: error?.status || null,
          code: error?.code || null,
        });
        setError(error.message);
        queuedFirstPageRefreshRef.current = false;

        if (page === 1 && !append && isTemporaryRefreshError(error)) {
          refreshFailureCountRef.current += 1;
          const exponentialDelay = Math.min(
            NOTIFICATION_REFRESH_INTERVAL_MS * (2 ** (refreshFailureCountRef.current - 1)),
            MAX_NOTIFICATION_REFRESH_BACKOFF_MS,
          );
          const retryAfterMs = Number.isFinite(error?.retryAfterMs) ? error.retryAfterMs : 0;
          scheduleRefreshRef.current(Math.max(exponentialDelay, retryAfterMs));
        }

        throw error;
      } finally {
        if (refreshAbortControllerRef.current === controller) {
          refreshAbortControllerRef.current = null;
        }
        if (refreshInFlightRef.current === request) {
          refreshInFlightRef.current = null;
        }
        if (generation === lifecycleGenerationRef.current) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }

        if (
          queuedFirstPageRefreshRef.current
          && sessionActiveRef.current
          && authService.isAuthenticated()
          && isBrowserOnline()
        ) {
          queuedFirstPageRefreshRef.current = false;
          const currentGeneration = lifecycleGenerationRef.current;
          Promise.resolve().then(() => {
            fetchNotificationsRef.current?.(1, false, currentGeneration).catch(() => {
              // The request records its error and schedules a bounded retry.
            });
          });
        }
      }
    })();

    refreshInFlightRef.current = request;
    return request;
  }, []);

  fetchNotificationsRef.current = fetchNotifications;

  // Initialize notifications
  const initialize = useCallback(async () => {
    if (!authService.isAuthenticated()) {
      setError('User must be authenticated');
      return;
    }

    const generation = lifecycleGenerationRef.current;
    sessionActiveRef.current = true;
    refreshFailureCountRef.current = 0;

    setIsLoading(true);
    setError(null);

    let connectionError = null;
    let refreshError = null;
    try {
      // Start the localized REST refresh immediately. A slow or unavailable
      // WebSocket must not keep already-rendered notification copy in the old
      // language while its ticket/connection attempt is pending.
      const connectionPromise = notificationService.initialize().catch((error) => {
        connectionError = error;
        return null;
      });
      const notificationsPromise = fetchNotifications(1, false, generation).catch((error) => {
        refreshError = error;
        return null;
      });

      await Promise.all([connectionPromise, notificationsPromise]);
      if (generation !== lifecycleGenerationRef.current) return false;

      const status = notificationService.getConnectionStatus() || {};
      setIsConnected(status.connected === true);
      setConnectionStatus(status);
      if (connectionError) setError(connectionError.message);
      else if (refreshError) setError(refreshError.message);

      return status.connected === true;

    } catch (err) {
      if (generation !== lifecycleGenerationRef.current) return false;
      setError(err.message);
      setIsConnected(false);
      return false;
    } finally {
      if (generation === lifecycleGenerationRef.current) setIsLoading(false);
    }
  }, [fetchNotifications]);

  // Cleanup notifications
  const cleanup = useCallback(() => {
    lifecycleGenerationRef.current += 1;
    sessionActiveRef.current = false;
    queuedFirstPageRefreshRef.current = false;
    if (refreshTimerRef.current !== null) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    refreshAbortControllerRef.current?.abort();
    refreshAbortControllerRef.current = null;
    // Detach the cancelled generation immediately so the next authenticated
    // session does not join its stale promise.
    refreshInFlightRef.current = null;
    refreshFailureCountRef.current = 0;
    notificationService.cleanup();
    setIsConnected(false);
    setConnectionStatus({});
    setNotifications([]);
    setIsLoading(false);
    setError(null);
    setCurrentPage(1);
    setHasMore(false);
    setIsLoadingMore(false);
    liveNotificationSequenceRef.current = 0;
    liveNotificationsRef.current = [];
  }, []);

  // Reconnect to all channels
  const reconnect = useCallback(async () => {
    cleanup();
    await initialize();
  }, [initialize, cleanup]);

  // Set up notification listener
  useEffect(() => {
    const handleNotification = (event) => {
      const { type, data } = event.detail;

      // Handle individual notifications from WebSocket
      if (type === 'individual_notification' && data) {
        if (isHiddenNotification(data)) {
          return;
        }

        // This is a new notification from the backend
        const backendNotification = {
          id: data.id,
          notification_type: data.type || data.notification_type,
          title: data.title,
          message: data.message,
          data: data.data || {},
          is_read: data.is_read || false,
          created_at: data.created_at,
          read_at: data.read_at
        };

        const sequence = liveNotificationSequenceRef.current + 1;
        liveNotificationSequenceRef.current = sequence;
        liveNotificationsRef.current = [
          { sequence, notification: backendNotification },
          ...liveNotificationsRef.current.filter((entry) => (
            String(entry.notification?.id) !== String(backendNotification.id)
          )),
        ].slice(0, 150);

        // Add to local state
        setNotifications(prev => {
          // Check if notification already exists to avoid duplicates
          const exists = prev.find(n => String(n.id) === String(backendNotification.id));
          if (exists) {
            return prev; // Don't add duplicate
          }
          // Add new notification at the top and limit to backend max display count
          return [backendNotification, ...prev].slice(0, 150);
        });

      }

      // Handle notification state changes
      if (type === 'notification_marked_read' && data?.id) {
        setNotifications(prev =>
          prev.map(notification =>
            notification.id === data.id
              ? { ...notification, is_read: true, read_at: new Date().toISOString() }
              : notification
          )
        );
      }

      if (type === 'notification_dismissed' && data?.id) {
        setNotifications(prev => prev.filter(n => n.id !== data.id));
      }

      if (type === 'all_notifications_marked_read') {
        setNotifications(prev =>
          prev.map(notification => ({ ...notification, is_read: true, read_at: new Date().toISOString() }))
        );
      }

      if (type === 'all_notifications_dismissed') {
        setNotifications([]);
      }

      if (type === 'connection_established') {
        fetchNotificationsRef.current?.(
          1,
          false,
          lifecycleGenerationRef.current,
          { queueIfBusy: true },
        ).catch(() => {
          // The REST fallback owns its retry schedule and preserves old rows.
        });
      }

      // Update connection status periodically
      const status = notificationService.getConnectionStatus() || {};
      setConnectionStatus(status);
      setIsConnected(status.connected || false);
    };

    // Add event listener
    window.addEventListener('notification', handleNotification);
    notificationListenerRef.current = handleNotification;

    return () => {
      window.removeEventListener('notification', handleNotification);
    };
  }, []);

  // Periodic REST fallback. It intentionally does not depend on WebSocket
  // state: a healthy API must keep the list fresh while the socket is down.
  useEffect(() => {
    const clearRefreshTimer = () => {
      if (refreshTimerRef.current !== null) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };

    const scheduleRefresh = (delay) => {
      clearRefreshTimer();
      if (
        !sessionActiveRef.current
        || !authService.isAuthenticated()
        || !isBrowserOnline()
      ) return;

      refreshTimerRef.current = setTimeout(() => {
        refreshTimerRef.current = null;
        if (
          !sessionActiveRef.current
          || !authService.isAuthenticated()
          || !isBrowserOnline()
        ) return;

        fetchNotificationsRef.current?.(
          1,
          false,
          lifecycleGenerationRef.current,
          { queueIfBusy: true },
        ).catch(() => {
          // Temporary failures schedule their own backoff in the request path.
        });
      }, delay);
    };

    scheduleRefreshRef.current = scheduleRefresh;

    const handleOffline = () => {
      browserWasOfflineRef.current = true;
      clearRefreshTimer();
      refreshAbortControllerRef.current?.abort();
    };

    const handleOnline = () => {
      if (
        !browserWasOfflineRef.current
        || !sessionActiveRef.current
        || !authService.isAuthenticated()
      ) return;
      browserWasOfflineRef.current = false;
      refreshFailureCountRef.current = 0;
      clearRefreshTimer();
      fetchNotificationsRef.current?.(
        1,
        false,
        lifecycleGenerationRef.current,
        { queueIfBusy: true },
      ).catch(() => {
        // Temporary failures schedule their own backoff in the request path.
      });
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      clearRefreshTimer();
      scheduleRefreshRef.current = () => {};
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  // Load more notifications
  const loadMore = useCallback(async () => {
    if (hasMore && !isLoadingMore) {
      await fetchNotifications(currentPage + 1, true);
    }
  }, [currentPage, hasMore, isLoadingMore, fetchNotifications]);

  return {
    // Connection state
    isConnected,
    connectionStatus,
    isLoading,
    error,

    // Notifications
    notifications,
    unreadCount,

    // Pagination
    hasMore,
    isLoadingMore,
    currentPage,
    loadMore,

    // Actions
    initialize,
    cleanup,
    reconnect,
    removeNotification,
    clearNotifications,
    
    // Mark notification as read
    markAsRead: useCallback(async (id) => {
      try {
        // Mark as read in backend
        await notificationService.markNotificationAsRead(id);

        // Update local state
        setNotifications(prev =>
          prev.map(notification =>
            notification.id === id
              ? { ...notification, is_read: true, read_at: new Date().toISOString() }
              : notification
          )
        );
      } catch {
        console.error('Failed to mark notification as read');
        // Still update local state even if backend fails
        setNotifications(prev =>
          prev.map(notification =>
            notification.id === id
              ? { ...notification, is_read: true, read_at: new Date().toISOString() }
              : notification
          )
        );
      }
    }, []),
    
    // Mark all as read
    markAllAsRead: useCallback(async () => {
      try {
        await notificationService.markAllNotificationsAsRead();

        setNotifications(prev =>
          prev.map(notification => ({ ...notification, is_read: true, read_at: new Date().toISOString() }))
        );
      } catch {
        console.error('Failed to mark all notifications as read');
        // Still update local state even if backend fails
        setNotifications(prev =>
          prev.map(notification => ({ ...notification, is_read: true, read_at: new Date().toISOString() }))
        );
      }
    }, []),
    
    // Get notifications by type
    getNotificationsByType: useCallback((type) => {
      return notifications.filter(notification => notification.type === type);
    }, [notifications]),

    // Get notifications by category
    getNotificationsByCategory: useCallback((category) => {
      return notifications.filter(notification => notification.category === category);
    }, [notifications]),

    // Get unread notifications by priority
    getUnreadByPriority: useCallback((priority) => {
      return notifications.filter(notification => !notification.read && notification.priority === priority);
    }, [notifications]),

    // Get notifications by type
    getNotificationsByType: useCallback((type) => {
      return notifications.filter(notification => notification.notification_type === type || notification.type === type);
    }, [notifications]),

    // Get unread notifications
    getUnreadNotifications: useCallback(() => {
      return notifications.filter(notification => !notification.is_read);
    }, [notifications]),

    // Get document expiration notifications
    getDocumentNotifications: useCallback(() => {
      return notifications.filter(notification =>
        notification.notification_type === 'document_expiration' ||
        notification.type === 'document_expiration'
      );
    }, [notifications]),

    // Get driver status notifications
    getDriverStatusNotifications: useCallback(() => {
      return notifications.filter(notification =>
        notification.notification_type === 'driver_status_change' ||
        notification.type === 'driver_status_change'
      );
    }, [notifications]),

    // Refresh notifications from server
    refreshNotifications: fetchNotifications,

    // Get read/unread counts by type
    getNotificationStats: useCallback(() => {
      const stats = {
        total: notifications.length,
        unread: notifications.filter(n => !n.is_read).length,
        by_type: {}
      };

      notifications.forEach(n => {
        const type = n.notification_type || n.type || 'unknown';
        if (!stats.by_type[type]) {
          stats.by_type[type] = { total: 0, unread: 0 };
        }
        stats.by_type[type].total++;
        if (!n.is_read) {
          stats.by_type[type].unread++;
        }
      });

      return stats;
    }, [notifications])
  };
};
